import React, { useState } from 'react';
import StageShell from './common/StageShell';
import { getProject, setStage } from '../../services/sdlcProject.service';
import { synthesizeDocs } from './sdlcSynthesizers';

/**
 * SDLC – Documentation stage.
 *
 * Aggregates the project's idea, requirements, architecture, code, tests
 * and deployment artefacts into a project README plus an Architecture
 * Decision Record (ADR).
 */
function DocsStage() {
  const [project, setProject] = useState(getProject());
  const docs = project?.docs;
  const [activeFile, setActiveFile] = useState(0);

  const handleGenerate = () => {
    const result = synthesizeDocs(getProject());
    const next = setStage('docs', result);
    setProject(next);
    setActiveFile(0);
  };

  const handleDownload = (file) => {
    const blob = new Blob([file.content], { type: 'text/markdown;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = file.path.replace(/^.*[\\/]/, '');
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const inputs = [
    { label: 'Idea', value: project?.idea?.title || '' },
    { label: 'Architecture', value: project?.architecture?.options?.find((o) => o.id === project?.architecture?.selectedId)?.name || '' },
    { label: 'Code', value: project?.code?.snapshot ? `${(project.code.snapshot.files || []).length} files` : '' },
    { label: 'Tests', value: project?.tests?.files?.length ? `${project.tests.files.length} files (${project.tests.framework})` : '' },
    { label: 'Deployment', value: project?.deployment?.provider || '' },
  ];

  const files = docs?.files || [];

  return (
    <StageShell
      stageId="docs"
      description="Aggregate the entire SDLC project into README + ADRs ready to drop into your repo."
      inputs={inputs}
      canContinue={files.length > 0}
      continueLabel="Done"
      actions={<button className="stage-btn primary" onClick={handleGenerate}>
        {docs ? 'Regenerate docs' : 'Generate docs'}
      </button>}
    >
      {!docs && (
        <div className="stage-empty">
          <div className="icon">📚</div>
          Click <em>Generate docs</em> to assemble README and ADRs from upstream stages.
        </div>
      )}

      {files.length > 0 && (
        <div style={{ display: 'grid', gridTemplateColumns: '260px 1fr', gap: 12, marginTop: 12 }}>
          <div className="stage-file-list" style={{ marginTop: 0 }}>
            {files.map((f, i) => (
              <div key={f.path} style={{ display: 'flex', gap: 6 }}>
                <button
                  type="button"
                  className="stage-file-item"
                  style={{
                    textAlign: 'left',
                    cursor: 'pointer',
                    background: activeFile === i ? '#eff6ff' : 'transparent',
                    flex: 1,
                  }}
                  onClick={() => setActiveFile(i)}
                >
                  <span>{f.path}</span>
                </button>
                <button
                  type="button"
                  className="stage-btn"
                  onClick={() => handleDownload(f)}
                  title="Download this file"
                >
                  ⬇
                </button>
              </div>
            ))}
          </div>
          <pre className="stage-pre" style={{ whiteSpace: 'pre-wrap' }}>{files[activeFile]?.content}</pre>
        </div>
      )}
    </StageShell>
  );
}

export default DocsStage;
